import CustomModal from "@/shared/components/ui/Modal/CustomModal";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { X } from "lucide-react";
import { addContactFormSchema } from "../formSchema/addContactFormSchema";
import AddContactForm from "./AddContactForm";
import { useUpdateContact } from "../hooks/useContacts";
import { showErrorFromMutation } from "@/shared/utils/toast";
import useUnsavedChangesWarning from "@/shared/hooks/useUnsavedChangesWarning";

function mapUpdateContactServerError(message) {
  if (!message || typeof message !== "string") {
    return { field: null, message: "Failed to update contact. Please try again." };
  }
  const lower = message.toLowerCase();
  if (lower.includes("whatsapp")) {
    return { field: "whatsappNumber", message };
  }
  if (lower.includes("email")) {
    return { field: "email", message };
  }
  return { field: null, message };
}

const EditContactModal = ({ isOpen, onClose, contact, inline = false }) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty },
    setValue,
    watch,
    setError,
    clearErrors,
  } = useForm({
    mode: "onChange",
    resolver: zodResolver(addContactFormSchema),
    defaultValues: {
      fullName: "",
      designation: "",
      company: "",
      linkedinUrl: "",
      companySize: "",
      email: "",
      whatsappNumber: "",
      industry: [],
      country: [],
      companyLinkedinUrl: "",
      companyWebsite: "",
      leadSource: "",
      socialLeadId: "",
      buyingRole: "",
      persona: "",
      description: "",
      emailOptOut: false,
    },
  });

  const { mutate: updateContact, isPending: isUpdating } = useUpdateContact();

  useEffect(() => {
    if (contact && isOpen) {
      // split industry / country strings back into arrays for onboarding select dropdown
      const industryArray = contact.targetIndustries
        ? [contact.targetIndustries]
        : contact.industry && contact.industry !== "Not Available"
        ? [contact.industry]
        : [];
      
      const countryArray = contact.location
        ? [contact.location]
        : [];

      reset({
        fullName: contact.name || "",
        designation: contact.position || "",
        company: contact.company || "",
        linkedinUrl: contact.linkedinUrl || "",
        companySize: contact.companySize && contact.companySize !== "N/A" ? contact.companySize : "",
        email: contact.email || "",
        whatsappNumber: contact.whatsappNumber && contact.whatsappNumber !== "N/A" ? contact.whatsappNumber : "",
        industry: industryArray,
        country: countryArray,
        companyLinkedinUrl: contact.companyLinkedinUrl || "",
        companyWebsite: contact.companyWebsite || "",
        leadSource: contact.leadSource || "",
        socialLeadId: contact.socialLeadId || "",
        buyingRole: contact.buyingRole || "",
        persona: contact.persona || "",
        description: contact.description || "",
        emailOptOut: Boolean(contact.emailOptOut),
      });
    }
  }, [contact, isOpen, reset]);

  const onSubmit = (data) => {
    clearErrors(["email", "whatsappNumber"]);
    const industryJoined = Array.isArray(data.industry)
      ? data.industry.map(String).filter(Boolean).join(", ")
      : String(data.industry ?? "");
    const countryJoined = Array.isArray(data.country)
      ? data.country.map(String).filter(Boolean).join(", ")
      : String(data.country ?? "");

    updateContact(
      {
        contactId: contact.id,
        contactData: {
          ...data,
          industry: industryJoined,
          country: countryJoined,
        },
      },
      {
        onSuccess: () => {
          handleClose();
        },
        onError: (error) => {
          const raw = error?.message || "";
          const { field, message } = mapUpdateContactServerError(raw);
          if (field) {
            setError(field, { type: "server", message });
          } else {
            showErrorFromMutation(error, message);
          }
        },
      },
    );
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  useUnsavedChangesWarning(Boolean(isOpen && isDirty));

  const form = (
    <AddContactForm
      handleSubmit={handleSubmit}
      onSubmit={onSubmit}
      register={register}
      control={control}
      errors={errors}
      isSubmitting={isUpdating}
      onCancel={handleClose}
      setValue={setValue}
      watch={watch}
      submitText="Save Changes"
      variant={inline ? "detail" : "default"}
    />
  );

  if (inline) {
    if (!isOpen) return null;
    return (
      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50/70 px-5 py-4 dark:border-gray-800 dark:bg-gray-800/30">
          <div>
            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
              Edit Contact
            </h2>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Keep this contact profile accurate across your campaigns.
            </p>
          </div>
          <button
            type="button"
            className="rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
            onClick={handleClose}
          >
            Cancel
          </button>
        </div>
        <div className="p-5">{form}</div>
      </section>
    );
  }

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleClose}
      width="500px"
      className="p-6"
    >
      <div className="w-full flex flex-col gap-4">
        <div className="flex justify-between items-start w-full">
          <h2 className="text-[0.9825rem] font-semibold text-gray-800 dark:text-gray-100">
            Edit Contact
          </h2>
          <button
            type="button"
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
            onClick={handleClose}
          >
            <X size={20} className="text-gray-800 dark:text-gray-200" />
          </button>
        </div>
        {form}
      </div>
    </CustomModal>
  );
};

export default EditContactModal;
